import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { CalendarDays, MessageCircle, CheckCircle, ShieldCheck, Star } from 'lucide-react'

/* ── Desktop kinetic reveal variants ── */
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
}
const lineVariants = {
  hidden: { y: '110%', opacity: 0 },
  visible: {
    y: '0%',
    opacity: 1,
    transition: { duration: 0.85, ease: [0.25, 0.46, 0.45, 0.94] },
  },
}

const HeroSection = () => {
  const heroRef = useRef(null)
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] })
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 90])

  return (
    <section ref={heroRef} id="hero" data-testid="hero-section" className="relative">

      {/* ══════════════════════════════
          MOBILE — Fullscreen cinematic
      ══════════════════════════════ */}
      <div className="lg:hidden relative min-h-screen flex flex-col justify-end overflow-hidden">

        {/* Full-bleed background image */}
        <img
          src="/images/hero.webp"
          alt="Profissional Marido de Aluguel"
          data-testid="hero-image-mobile"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />

        {/* Cinematic gradient: transparent top → navy bottom */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#062B63] via-[#062B63]/65 to-[#062B63]/5" />

        {/* Content anchored at bottom */}
        <motion.div
          className="relative z-10 px-6 pb-14 pt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Badge */}
          <span className="inline-flex items-center gap-2 border border-[#F2B300]/50 bg-[#F2B300]/15 text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] mb-5 backdrop-blur-sm">
            <CheckCircle size={12} className="text-[#F2B300]" />
            Soluções práticas para o seu dia a dia
          </span>

          {/* H1 */}
          <h1 className="text-[2.5rem] sm:text-5xl font-bold text-white leading-[0.93] tracking-tight font-outfit">
            Serviços práticos com
          </h1>
          <h1 className="text-[2.5rem] sm:text-5xl font-bold text-[#F2B300] leading-[0.93] tracking-tight font-outfit mb-5">
            agilidade e confiança.
          </h1>

          {/* Description */}
          <p
            data-testid="hero-description-mobile"
            className="text-white/70 text-sm leading-relaxed mb-7 max-w-[320px] mx-auto"
          >
            Atendimento profissional para sua casa, empresa e necessidades do dia a dia.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col gap-3 mb-7">
            <a
              href="#formulario"
              data-testid="hero-pedir-orcamento-btn"
              className="flex items-center justify-center gap-2 bg-[#F2B300] text-[#062B63] px-8 py-4 text-sm font-bold tracking-wide active:brightness-90"
            >
              <CalendarDays size={16} />
              Pedir orçamento
            </a>
            <a
              href="https://wa.me/5547988114676"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="flex items-center justify-center gap-2 border-2 border-white/60 text-white px-8 py-4 text-sm font-bold tracking-wide active:bg-white/10"
            >
              <MessageCircle size={16} />
              Falar no WhatsApp
            </a>
          </div>

          {/* Trust signals */}
          <div className="flex items-center justify-center gap-5 text-xs text-white/55">
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={13} className="text-white/50" />
              Serviço garantido
            </span>
            <span className="flex items-center gap-1.5">
              <Star size={13} className="fill-[#F2B300] text-[#F2B300]" />
              Avaliações 5 estrelas
            </span>
          </div>
        </motion.div>
      </div>

      {/* ══════════════════════════════
          DESKTOP — Split layout
      ══════════════════════════════ */}
      <div className="hidden lg:block relative min-h-screen bg-[#F7F7F7] overflow-hidden pt-16">

        {/* Decorative bg */}
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-[#F2B300]/4 -translate-y-1/2 translate-x-1/2 rounded-full -z-10" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#062B63]/10 to-transparent" />

        <div className="max-w-7xl mx-auto px-8 min-h-[calc(100vh-64px)] flex items-center">
          <div className="grid grid-cols-2 gap-0 w-full items-center py-20">

            {/* Left: Kinetic text reveal */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="relative z-10 pr-12"
            >
              {/* Badge */}
              <div className="overflow-hidden mb-6">
                <motion.div variants={lineVariants}>
                  <span
                    data-testid="hero-badge"
                    className="inline-flex items-center gap-2 border border-[#F2B300]/60 bg-[#F2B300]/10 text-[#062B63] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em]"
                  >
                    <CheckCircle size={13} className="text-[#F2B300]" />
                    Soluções práticas para o seu dia a dia
                  </span>
                </motion.div>
              </div>

              {/* H1 Line 1 */}
              <div className="overflow-hidden">
                <motion.h1
                  variants={lineVariants}
                  className="text-[clamp(3rem,5vw,5rem)] font-bold text-[#062B63] leading-[0.93] tracking-tight font-outfit"
                >
                  Serviços práticos com
                </motion.h1>
              </div>

              {/* H1 Line 2 */}
              <div className="overflow-hidden mb-8">
                <motion.h1
                  variants={lineVariants}
                  className="text-[clamp(3rem,5vw,5rem)] font-bold text-[#F2B300] leading-[0.93] tracking-tight font-outfit"
                >
                  agilidade e confiança.
                </motion.h1>
              </div>

              {/* Description */}
              <div className="overflow-hidden mb-8">
                <motion.p
                  variants={lineVariants}
                  data-testid="hero-description"
                  className="text-[#062B63]/65 text-lg leading-relaxed max-w-[480px] font-inter"
                >
                  Atendimento profissional para sua casa, empresa e necessidades do dia a dia. Soluções com qualidade, transparência e preço justo.
                </motion.p>
              </div>

              {/* Highlights */}
              <div className="overflow-hidden mb-9">
                <motion.div variants={lineVariants} className="flex flex-wrap gap-5">
                  {['Atendimento rápido', 'Serviço organizado', 'Orçamento claro'].map((h) => (
                    <span key={h} className="flex items-center gap-1.5 text-sm text-[#062B63] font-semibold">
                      <CheckCircle size={15} className="text-[#F2B300] flex-shrink-0" />
                      {h}
                    </span>
                  ))}
                </motion.div>
              </div>

              {/* CTA Buttons */}
              <div className="overflow-hidden mb-10">
                <motion.div variants={lineVariants} className="flex flex-wrap gap-4">
                  <a
                    href="#formulario"
                    data-testid="hero-pedir-orcamento-btn-desktop"
                    className="inline-flex items-center gap-2 bg-[#F2B300] text-[#062B63] px-8 py-4 text-sm font-bold tracking-wide hover:brightness-95 transition-all active:scale-[0.98]"
                  >
                    <CalendarDays size={17} />
                    Pedir orçamento
                  </a>
                  <a
                    href="https://wa.me/5547988114676"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-whatsapp-btn-desktop"
                    className="inline-flex items-center gap-2 border-2 border-[#062B63] text-[#062B63] px-8 py-4 text-sm font-bold tracking-wide hover:bg-[#062B63] hover:text-white transition-all active:scale-[0.98]"
                  >
                    <MessageCircle size={17} />
                    Falar no WhatsApp
                  </a>
                </motion.div>
              </div>

              {/* Trust signals */}
              <div className="overflow-hidden">
                <motion.div
                  variants={lineVariants}
                  className="flex items-center gap-6 text-sm text-[#062B63]/55 pt-6 border-t border-[#062B63]/10"
                >
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck size={15} className="text-[#062B63]/50" />
                    Serviço garantido
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Star size={14} className="text-[#F2B300] fill-[#F2B300]" />
                    Avaliações 5 estrelas
                  </span>
                </motion.div>
              </div>
            </motion.div>

            {/* Right: Image with parallax */}
            <motion.div
              className="relative flex items-end justify-center h-full min-h-[600px]"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <motion.div style={{ y: imageY }} className="relative w-full flex items-end justify-end">
                <div className="absolute top-24 right-10 w-px h-28 bg-[#F2B300]" />
                <div className="absolute top-20 right-6 w-8 h-8 border-2 border-[#F2B300]" />
                <div className="absolute bottom-40 left-16 w-14 h-14 border border-[#F2B300]/35" />
                <div className="absolute bottom-0 right-0 w-4/5 h-5/6 bg-[#062B63]/3 -z-10" />
                <img
                  src="/images/hero.webp"
                  alt="Profissional Marido de Aluguel"
                  data-testid="hero-image"
                  className="relative z-10 w-full max-h-[82vh] object-contain object-bottom drop-shadow-sm"
                />
              </motion.div>
            </motion.div>

          </div>
        </div>
      </div>

    </section>
  )
}

export default HeroSection
